<template>
  <div>
    <div class="flex md:flex-wrap gap-4 flex-row justify-between items-center">
      <div class="flex justify-between items-center">
        <div>
          <div class="page-header-1">
            {{ day }}
          </div>
          <div class="relative flex gap-4 items-center whitespace-nowrap">
            <div
              class="page-header-2 cursor-pointer"
              style="display: inline-flex; align-items: center; gap: 0.5rem"
              @click="toggleCalendarModal"
            >
              {{ title }}
              <i
                class="fa-regular fa-calendar"
                style="position: relative; top: -1px"
              />
            </div>
            <ButtonGroup
              class="hidden md:block"
              :buttons="[
                {
                  icon: 'fa-regular fa-chevron-left',
                  isIconOnly: true,
                  date: 'prev'
                },
                { label: 'Today', date: 'today' },
                {
                  icon: 'fa-regular fa-chevron-right',
                  isIconOnly: true,
                  date: 'next'
                }
              ]"
              data-cy="today-btn"
              :selected-highlight="false"
              @update:buttons="handleNavigate"
            />

            <div
              v-if="calendarModal"
              class="absolute top-full left-0 mt-2 z-50 w-80 rounded-lg shadow-lg bg-white border border-gray-200"
            >
              <ClickOutside
                :exclude-classes="['datepicker-cell']"
                @click-outside="toggleCalendarModal"
              >
                <div class="pt-2 pb-2">
                  <Calendar
                    ref="calendar"
                    v-model="calendarDate"
                    :min-date="null"
                  />
                  <div class="flex gap-3 mt-2 px-2">
                    <Button
                      type="Secondary"
                      label="Today"
                      class="w-1/3"
                      @click="selectToday"
                    />
                    <Button
                      label="Update"
                      class="w-2/3"
                      @click="handleDateChange"
                    />
                  </div>
                  <div
                    v-if="firstErrorMessage(v$.calendarDate)"
                    class="text-red-700 text-right mt-2 px-2"
                  >
                    {{ firstErrorMessage(v$.calendarDate) }}
                  </div>
                </div>
              </ClickOutside>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-3 md:hidden"></div>
      </div>

      <div class="flex gap-3 items-center justify-end md:flex-wrap">
        <div class="flex items-center gap-3 md:flex-row-reverse">
          <ScheduleLegendIcon />
        </div>
        <Fob
          type="Secondary"
          class="md:hidden group"
          @click="toggleAvailabilityModal"
        >
          <CalendarIcon class="w-4" />
        </Fob>
        <ScheduleFilterMenuTrigger />
        <Button
          class="hidden md:block group"
          outline
          @click="toggleAvailabilityModal"
        >
          <CalendarIcon class="w-5" />Check Availability
        </Button>
        <CreateEventButton :default-date="date" />
      </div>
    </div>
    <CheckAvailabilityModal
      v-if="availabiltyModal"
      @hide-modal="toggleAvailabilityModal"
    />
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { customDateFormatWithTimezone } from "@/lib/helpers";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Calendar from "@/components/Calendar.vue";
import ClickOutside from "@/components/ClickOutside.vue";
import ButtonGroup from "@/components/Seranova/ButtonGroups/ButtonGroup.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Fob from "@/components/Seranova/Fobs/Fob.vue";
import CheckAvailabilityModal from "@/CheckAvailabilityModal/CheckAvailabilityModal.vue";
import CalendarIcon from "@/images/CalendarDots.svg";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import CreateEventButton from "./CreateEventButton.vue";
import ScheduleFilterMenuTrigger from "./ScheduleFilterMenuTrigger.vue";
import ScheduleLegendIcon from "./ScheduleLegendIcon.vue";

const emit = defineEmits<{
  (e: "updateSelectedDate", date: Date): void;
  (e: "updateView", view: "timeline" | "capacity"): void;
}>();
const props = defineProps<{ date: Date; view: "timeline" | "capacity" }>();
const calendarDate = ref(props.date);
const calendarModal = ref(false);
const availabiltyModal = ref(false);
const calendar = ref(null);
const v$ = useVuelidate(
  {
    calendarDate: {
      required: helpers.withMessage("Please select a date", required)
    }
  },
  {
    calendarDate
  }
);

const toggleCalendarModal = () => (calendarModal.value = !calendarModal.value);

const handleDateChange = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  emit("updateSelectedDate", calendarDate.value);
  calendarModal.value = false;
};

const toggleAvailabilityModal = () => {
  availabiltyModal.value = !availabiltyModal.value;
};

const handleNavigate = (options) => {
  let newDate = new Date();
  const selected = options.find((option) => option.selected);
  switch (selected.date) {
    case "prev":
      newDate = moment(calendarDate.value).subtract(1, "day").toDate();
      break;
    case "next":
      newDate = moment(calendarDate.value).add(1, "day").toDate();
      break;
  }
  emit("updateSelectedDate", newDate);
};

const selectToday = () => (calendarDate.value = new Date());

watch(
  () => props.date,
  (newVal, oldVal) => {
    if (!moment(newVal).isSame(oldVal, "days")) {
      calendarDate.value = newVal;
    }
  }
);

const title = computed(() => {
  // Set selected date hours to 12 pm to handle timezone offsets
  const adjustedDate = moment(props.date).hours(12);
  return customDateFormatWithTimezone(adjustedDate, "MMMM DD, YYYY");
});
const day = computed(() => {
  // Set selected date hours to 12 pm to handle timezone offsets
  const adjustedDate = moment(props.date).hours(12);
  return customDateFormatWithTimezone(adjustedDate, "dddd");
});
</script>

<style scoped></style>
